import React from 'react';
import { Navigate } from 'react-router-dom';
import { useFeatureToggleStore } from '../stores/useFeatureToggleStore';
import { ModuleKey } from '../types/featureToggle';
import { AlertTriangle, ToggleLeft, Lock } from 'lucide-react';
import { useAuthStore } from '@/stores/useAuthStore';
import { Permission } from '@/types/user';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { homeRoute } from '@/config/homeRoute';
import { useShellIdentity } from '@/components/shell/useShellIdentity';


interface FeatureGuardProps {
  moduleKey: ModuleKey;
  featureKey?: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

// Map ModuleKey to required permissions
// Not every module requires a permission gate; consumers null-check the lookup.
const modulePermissions: Partial<Record<ModuleKey, Permission[]>> = {
  [ModuleKey.PATIENT_MANAGEMENT]: [Permission.PATIENT_READ],
  [ModuleKey.APPOINTMENTS]: [Permission.APPOINTMENT_READ],
  [ModuleKey.MEDICAL_RECORDS]: [Permission.MEDICAL_RECORDS_READ],
  [ModuleKey.LAB_ORDERS]: [Permission.LAB_ORDER_READ],
  [ModuleKey.BILLING]: [Permission.BILLING_READ],
  [ModuleKey.TEMPLATES]: [Permission.TEMPLATE_READ],
  [ModuleKey.CLINICAL_DECISION_SUPPORT]: [Permission.MEDICAL_RECORDS_READ],
  [ModuleKey.VOICE_AI]: [Permission.MEDICAL_RECORDS_READ],
};

export function FeatureGuard({
  moduleKey,
  featureKey,
  children,
  fallback,
  redirectTo = '/home'
}: FeatureGuardProps) {
  const { isModuleEnabled, isFeatureEnabled } = useFeatureToggleStore();
  const { user, roleCheck } = useAuthStore();

  // Check if module is enabled
  const moduleEnabled = isModuleEnabled(moduleKey);

  // If checking a specific feature, check that too
  const featureEnabled = featureKey
    ? isFeatureEnabled(moduleKey, featureKey)
    : true;

  // Check if user has required permissions. Use the live `permissions`
  // array from /auth/me (via roleCheck.hasAnyPermission) — the static
  // `rolePermissions[user.primaryRole]` map drifted from the backend
  // ROLE_DEFINITIONS and was admitting users to routes the API then 403'd.
  let hasRequiredPermission = true;
  if (user && modulePermissions[moduleKey]) {
    hasRequiredPermission = roleCheck.hasAnyPermission(
      modulePermissions[moduleKey],
    );
  }

  const isEnabled = moduleEnabled && featureEnabled && hasRequiredPermission;

  if (!isEnabled) {
    if (fallback) {
      return <>{fallback}</>;
    }

    // Disabled modules/features deserve an explanation, not a silent
    // redirect — otherwise the user sees the route bounce to /home with
    // no signal that the module toggle is off, and reports it as a bug
    // (e.g. "Resume editing goes to home"). Always show the diagnostic
    // page when the failure is a disabled toggle. Only redirect when
    // the failure is a permissions one.
    if (!moduleEnabled || !featureEnabled) {
      return <DisabledFeaturePage moduleKey={moduleKey} />;
    }

    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    return <NoPermissionPage moduleKey={moduleKey} />;
  }

  return <>{children}</>;
}

function DisabledFeaturePage({ moduleKey }: { moduleKey: ModuleKey }) {
  /* Where "back" goes — the role's own first door under the design shell, not
     the legacy dashboard, which the design replaced. See homeRoute. */
  const { surface } = useShellIdentity();
  const modules = useFeatureToggleStore((state) => state.modules);
  const module = modules[moduleKey];
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 mb-4">
          <ToggleLeft className="h-8 w-8 text-amber-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('featureGuard.featureDisabled')}
        </h2>
        <p className="text-gray-600 mb-6">
          {t('featureGuard.moduleDisabled', { moduleName: module?.name || moduleKey })}
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-blue-600 me-2 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800 text-start">
              {t('featureGuard.featureUnavailable')}
            </p>
          </div>
        </div>
        <Button asChild>
          <a href={homeRoute(surface)}>
            {t('featureGuard.returnToDashboard')}
          </a>
        </Button>
      </div>
    </div>
  );
}

function NoPermissionPage({ moduleKey }: { moduleKey: ModuleKey }) {
  /* Where "back" goes — the role's own first door under the design shell, not
     the legacy dashboard, which the design replaced. See homeRoute. */
  const { surface } = useShellIdentity();
  const modules = useFeatureToggleStore((state) => state.modules);
  const module = modules[moduleKey];
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
          <Lock className="h-8 w-8 text-red-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('featureGuard.accessDenied')}
        </h2>
        <p className="text-gray-600 mb-6">
          {t('featureGuard.noPermission', { moduleName: module?.name || moduleKey })}
        </p>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-red-600 me-2 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-red-800 text-start">
              {t('featureGuard.noPermissionMessage')}
            </p>
          </div>
        </div>
        <Button asChild>
          <a href={homeRoute(surface)}>
            {t('featureGuard.returnToDashboard')}
          </a>
        </Button>
      </div>
    </div>
  );
}
